import { Box, Group, Progress, Stack, Text, Tooltip as MantineTooltip, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BlackTechStreetData, latest, pctChange, previous } from '../types';
import { getMultiGoalStatus, getAnnualPaceState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function EventsPulseCard({ data }: { data: BlackTechStreetData }) {
  const theme = useMantineTheme();
  const series = data.events;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  
  // Calculate total events for headline metrics
  const totalThisQuarter = (cur?.communityEngagementLarge ?? 0) + (cur?.educationLarge ?? 0) + (cur?.educationSmall ?? 0);
  const totalPrevQuarter = prev ? prev.communityEngagementLarge + prev.educationLarge + prev.educationSmall : null;
  const totalQoq = pctChange(totalThisQuarter, totalPrevQuarter);
  const qoqArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqLabel = (n: number | null): string => (n === null ? '— QoQ' : `${qoqArrow(n)}${Math.abs(n).toFixed(2)}% QoQ`);

  const ytdCommunityLarge = currentMeta
    ? series
        .filter((p) => {
          const meta = parseQuarter(p.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((acc, p) => acc + p.communityEngagementLarge, 0)
    : 0;
  const ytdEducationLarge = currentMeta
    ? series
        .filter((p) => {
          const meta = parseQuarter(p.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((acc, p) => acc + p.educationLarge, 0)
    : 0;
  const ytdEducationSmall = currentMeta
    ? series
        .filter((p) => {
          const meta = parseQuarter(p.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((acc, p) => acc + p.educationSmall, 0)
    : 0;
  
  const totalYtd = currentMeta
    ? series
        .filter((p) => {
          const meta = parseQuarter(p.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((acc, p) => acc + p.communityEngagementTotal + p.educationTotal, 0)
    : 0;
  
  const totalYoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((p) => {
        const meta = parseQuarter(p.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((acc, p) => acc + p.communityEngagementTotal + p.educationTotal, 0);
    return pctChange(totalYtd, prevYearTotal);
  })();
  const yoyLabel = (n: number | null): string => (n === null ? '— YoY' : `${qoqArrow(n)}${Math.abs(n).toFixed(2)}% YoY`);
  
  // Calculate YoY for each category
  const communityYoy = (() => {
    if (!currentMeta) return null;
    const prevYearCommunity = series
      .filter((p) => {
        const meta = parseQuarter(p.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((acc, p) => acc + p.communityEngagementLarge, 0);
    return pctChange(ytdCommunityLarge, prevYearCommunity);
  })();
  
  const educationLargeYoy = (() => {
    if (!currentMeta) return null;
    const prevYearEducationLarge = series
      .filter((p) => {
        const meta = parseQuarter(p.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((acc, p) => acc + p.educationLarge, 0);
    return pctChange(ytdEducationLarge, prevYearEducationLarge);
  })();
  
  const educationSmallYoy = (() => {
    if (!currentMeta) return null;
    const prevYearEducationSmall = series
      .filter((p) => {
        const meta = parseQuarter(p.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((acc, p) => acc + p.educationSmall, 0);
    return pctChange(ytdEducationSmall, prevYearEducationSmall);
  })();

  const totalAllTime =
    data.eventsAllTime.communityEngagementTotal +
    data.eventsAllTime.educationTotal;

  // Goal ranges
  const communityGoal = { min: 2, max: 4 };
  const educationLargeGoal = { min: 2, max: 4 };
  const educationSmallGoal = { min: 4, max: 8 };

  const status = getMultiGoalStatus([
    getAnnualPaceState({
      ytd: ytdCommunityLarge,
      goalPerYear: communityGoal.min,
      quarterLabel: cur?.quarter ?? 'Q1 1970',
      direction: 'min',
    }),
    getAnnualPaceState({
      ytd: ytdEducationLarge,
      goalPerYear: educationLargeGoal.min,
      quarterLabel: cur?.quarter ?? 'Q1 1970',
      direction: 'min',
    }),
    getAnnualPaceState({
      ytd: ytdEducationSmall,
      goalPerYear: educationSmallGoal.min,
      quarterLabel: cur?.quarter ?? 'Q1 1970',
      direction: 'min',
    }),
  ]);
  
  // Status for each category
  const getCategoryStatus = (ytd: number, goalRange: { min: number; max: number }) => {
    if (ytd >= goalRange.min && ytd <= goalRange.max) return { label: 'On Track', color: 'green' as const };
    if (ytd < goalRange.min) return { label: 'Below Goal', color: 'yellow' as const };
    return { label: 'Above Goal', color: 'blue' as const };
  };
  
  const communityStatus = getCategoryStatus(ytdCommunityLarge, communityGoal);
  const educationLargeStatus = getCategoryStatus(ytdEducationLarge, educationLargeGoal);
  const educationSmallStatus = getCategoryStatus(ytdEducationSmall, educationSmallGoal);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const chartData = series.map((p) => ({
    quarter: p.quarter,
    communityLarge: p.communityEngagementLarge,
    educationLarge: p.educationLarge,
    educationSmall: p.educationSmall,
  }));

  const tooltipShellStyleForProgress = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '8px 10px',
    fontSize: 12,
    maxWidth: 280,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell
      title="Events"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        {/* Headline Metrics */}
        <MetricRow
          items={[
            {
              label: 'Total Events (This Quarter)',
              labelTooltip: 'Total events this quarter (Community 100+ attendees + Education 100+ attendees + Education less than 50 attendees).',
              value: (
                <Text size="lg" weight={800}>
                  {totalThisQuarter}
                </Text>
              ),
              sublabel: qoqLabel(totalQoq),
            },
            {
              label: 'Total Events (YTD)',
              labelTooltip: 'Calendar year to date total events (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {totalYtd}
                </Text>
              ),
              sublabel: yoyLabel(totalYoy),
            },
            {
              label: 'Total Events (All-Time)',
              labelTooltip: 'Total events across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {totalAllTime}
                </Text>
              ),
            },
          ]}
        />

        {/* Goal Progress Indicators */}
        <Stack spacing="md">
          {/* Community Engagement */}
          <Stack spacing={4}>
            <Group position="apart" spacing="md" noWrap>
              <MantineTooltip
                label={`Community Engagement events with 100+ attendees. Goal: ${communityGoal.min}–${communityGoal.max} per year.`}
                styles={{ tooltip: tooltipShellStyleForProgress as any }}
              >
                <Text size="xs" color="dimmed" weight={700} sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
                  Community Engagement (≥100) YTD
                </Text>
              </MantineTooltip>
              <Text size="xs" color="dimmed">
                Annual Goal: {communityGoal.min}
              </Text>
            </Group>
            <MantineTooltip
              label={
                <div>
                  <div>YTD: {ytdCommunityLarge}</div>
                  <div>{yoyLabel(communityYoy)}</div>
                </div>
              }
              styles={{ tooltip: tooltipShellStyleForProgress as any }}
            >
              <Progress
                value={Math.min(100, (ytdCommunityLarge / communityGoal.min) * 100)}
                color={communityStatus.color === 'green' ? 'green' : 'yellow'}
                size="lg"
                radius="xl"
                sx={{ cursor: 'pointer' }}
              />
            </MantineTooltip>
          </Stack>

          {/* Education Large */}
          <Stack spacing={4}>
            <Group position="apart" spacing="md" noWrap>
              <MantineTooltip
                label={`Education & Training events with 100+ attendees. Goal: ${educationLargeGoal.min}–${educationLargeGoal.max} per year.`}
                styles={{ tooltip: tooltipShellStyleForProgress as any }}
              >
                <Text size="xs" color="dimmed" weight={700} sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
                  Education & Training (≥100) YTD
                </Text>
              </MantineTooltip>
              <Text size="xs" color="dimmed">
                Annual Goal: {educationLargeGoal.min}
              </Text>
            </Group>
            <MantineTooltip
              label={
                <div>
                  <div>YTD: {ytdEducationLarge}</div>
                  <div>{yoyLabel(educationLargeYoy)}</div>
                </div>
              }
              styles={{ tooltip: tooltipShellStyleForProgress as any }}
            >
              <Progress
                value={Math.min(100, (ytdEducationLarge / educationLargeGoal.min) * 100)}
                color={educationLargeStatus.color === 'green' ? 'green' : 'yellow'}
                size="lg"
                radius="xl"
                sx={{ cursor: 'pointer' }}
              />
            </MantineTooltip>
          </Stack>

          {/* Education Small */}
          <Stack spacing={4}>
            <Group position="apart" spacing="md" noWrap>
              <MantineTooltip
                label={`Education & Training events with less than 50 attendees. Goal: ${educationSmallGoal.min}–${educationSmallGoal.max} per year.`}
                styles={{ tooltip: tooltipShellStyleForProgress as any }}
              >
                <Text size="xs" color="dimmed" weight={700} sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
                  Education & Training (&lt;50) YTD
                </Text>
              </MantineTooltip>
              <Text size="xs" color="dimmed">
                Annual Goal: {educationSmallGoal.min}
              </Text>
            </Group>
            <MantineTooltip
              label={
                <div>
                  <div>YTD: {ytdEducationSmall}</div>
                  <div>{yoyLabel(educationSmallYoy)}</div>
                </div>
              }
              styles={{ tooltip: tooltipShellStyleForProgress as any }}
            >
              <Progress
                value={Math.min(100, (ytdEducationSmall / educationSmallGoal.min) * 100)}
                color={educationSmallStatus.color === 'green' ? 'green' : 'yellow'}
                size="lg"
                radius="xl"
                sx={{ cursor: 'pointer' }}
              />
            </MantineTooltip>
          </Stack>
        </Stack>

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Quarterly Events by Category
        </Text>

        <Box sx={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
              <XAxis
                dataKey="quarter"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={40}
                tickMargin={8}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  if (!p) return null;

                  const cLarge = Number(p.communityLarge ?? 0);
                  const eLarge = Number(p.educationLarge ?? 0);
                  const eSmall = Number(p.educationSmall ?? 0);
                  const total = cLarge + eLarge + eSmall;

                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2, marginBottom: 6 }}>
                        {total} Total Events
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9, lineHeight: 1.35 }}>
                        Community (≥100): {cLarge}
                        <br />
                        Education (≥100): {eLarge}
                        <br />
                        Education (&lt;50): {eSmall}
                      </div>
                    </div>
                  );
                }}
              />

              <Bar dataKey="communityLarge" name="Community (≥100)" fill={theme.colors.blue[6]} radius={[6, 6, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="educationLarge" name="Education (≥100)" fill={theme.colors.teal[6]} radius={[6, 6, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="educationSmall" name="Education (&lt;50)" fill={theme.colors.gray[6]} radius={[6, 6, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 12px',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 12,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: theme.colors.blue[6],
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Community (≥100)
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: theme.colors.teal[6],
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Education (≥100)
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: theme.colors.gray[6],
                flex: '0 0 auto',
              }}
            />
            <Text size="xs" color="dimmed">
              Education (&lt;50)
            </Text>
          </Box>
        </Box>
      </Stack>
    </CardShell>
  );
}
